
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import {
  algoliaToHighlightedProductCardProps,
  attraqtToHighlightedProductCardProps,
} from '@landing/utils/adapters'
import {
  getAdvertisingOptionsFromComponent,
  hasSearchPaymentAdvertising,
} from '@payment'
import { logger } from '@recommendation/helpers'
import recommendationAPIClient from '@recommendation/recommendationAPIClient'
import {
  createAttributes,
  toProductThumb,
  toTrackingData,
} from '@recommendation/recommendationAdapter'
import { setRecommendationDebugExtension } from '@recommendation/recommendationHelper'
import { ROUTES } from '@router'
import {
  EVENT_TYPE,
  PRODUCT_DEAL_TYPE,
  PRODUCT_PROVIDERS,
  PRODUCT_SOURCE,
  PRODUCT_SOURCE_TYPE,
} from '@tracking'
import {
  trackLandingPage,
  trackProductClick,
  trackSearchAnalytics,
} from '@tracking/events'

import SearchParams from '../../utils/SearchParams'
import { buildResultsFromAttributes } from '../ProductThumb'
import { createAttributesOld } from '../ProductThumb/helpers'

import CardsList from './CardsList.vue'
import translations from './ProductResults.translations'
import { LANDING_TYPES } from './constants'

const NUMBER_OF_RECOMMENDATIONS = 4
const OFFSET = 1

export default {
  components: {
    CardsList,
  },

  props: {
    isLoading: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      recommendationId: null,
      recommendations: [],
      recommendationWidgetId: '',
    }
  },
  async fetch() {
    if (this.isProductLanding) {
      // TODO: [SR-2292] Clean AB test
      switch (this.experiments?.removeFreshProducts) {
        case 'withoutFreshProducts':
          break
        case 'withFreshProducts':
        case 'noVariation':
        default:
          try {
            const apiClient = recommendationAPIClient((request) =>
              this.$store.dispatch('http/request', request),
            )

            const {
              widgetId,
              recommendationRequestId: recommendationId,
              products: recommendations,
            } = await apiClient.getRecommendedProducts({
              limit: NUMBER_OF_RECOMMENDATIONS,
              scope: 'landing',
              scopeId: this.landingId,
            })

            this.recommendationId = recommendationId
            this.recommendationWidgetId = widgetId
            this.recommendations = recommendations
          } catch (err) {
            logger.info(err, {
              component: { name: 'ProductResults', props: this.$props },
              routeName: this.$route.name,
            })

            this.recommendationId = null
            this.recommendationWidgetId = ''
            this.recommendations = []
          }
          break
      }
    }
  },
  computed: {
    translations() {
      return translations
    },

    ...mapGetters({
      buyingGuide: 'landing/buyingGuide',
      country: 'config/country',
      experiments: 'flags/getExperiments',
      filterWhitelist: 'landingSearch/filterWhitelist',
      isListView: 'landing/isListView',
      landingId: 'landing/landingId',
      landingType: 'landing/type',
      listViewStructure: 'landing/listViewStructure',
      marketingContent: 'landing/marketingContent',
      paymentMethods: 'countryConfig/paymentMethods',
      searchQueryID: 'landingSearch/queryID',
      searchResultsData: 'landingSearch/results',
      sortDefaultValue: 'landingSearch/sortDefaultValue',
      visitorId: 'config/visitorId',
      searchIsLoading: 'landingSearch/isLoading',
    }),

    showAdvert() {
      return (
        this.searchResults?.length > 0 &&
        !isEmpty(this.marketingContent) &&
        ((!isEmpty(this.marketingContent.card) && !this.isListViewDisplay) ||
          (!isEmpty(this.marketingContent.list) && this.isListViewDisplay))
      )
    },

    hasEnoughProductsForBuyingGuidesOrPaymentMethodAds() {
      return (
        (this.showAdvert && this.searchResults?.length >= 10) ||
        (!this.showAdvert && this.searchResults?.length >= 5)
      )
    },

    showBuyingGuide() {
      return (
        this.hasEnoughProductsForBuyingGuidesOrPaymentMethodAds &&
        this.buyingGuide &&
        Object.entries(this.buyingGuide).length > 0
      )
    },

    showPaymentMethodAdvertising() {
      const hasBuyingGuideAndAdvert = this.showBuyingGuide && this.showAdvert

      return (
        this.hasEnoughProductsForBuyingGuidesOrPaymentMethodAds &&
        this.paymentMethods &&
        !hasBuyingGuideAndAdvert &&
        hasSearchPaymentAdvertising(
          this.paymentMethods,
          getAdvertisingOptionsFromComponent(this),
        )
      )
    },

    isProductLanding() {
      return this.$route.name === ROUTES.LANDING.PRODUCT
    },

    isListViewDisplay() {
      return this.isListView && this.isProductLanding
    },

    linkToUse() {
      if (this.landingType === LANDING_TYPES.SELLER) {
        return 'merchant_link_grade_v2'
      }

      return 'link_grade_v2'
    },

    searchParams() {
      return new SearchParams({
        query: this.$route.query,
        hash: this.$route.hash,
        paramsWhitelist: this.filterWhitelist,
      })
    },

    hasFilters() {
      return this.searchParams.hasUserFilters()
    },

    highlightedOffSet() {
      return !isEmpty(this.highlightedProduct) ? 1 : 0
    },

    searchResultsOffSet() {
      return this.recommendationProducts.length + this.highlightedOffSet
    },

    trackingData() {
      const allResultsTrackingData = [this.highlightedProduct]
        .concat(this.recommendationProducts)
        .filter((product) => !isEmpty(product) && !product.isLoading)
        .concat(this.searchResults)
        .map((product, index) => {
          const position = this.calcProductPosition(index)

          const dealType = PRODUCT_DEAL_TYPE.NORMAL

          const productTrackingDataForAlgolia = {
            list: product.sourceType,
            product: {
              brand: product.brand,
              category: product.category,
              name: product.name || product.title,
              id: product.backmarketID,
              // TODO: URL-352 Use product.id when available in Algolia to get the UUID
              uuid: product.link?.params?.uuid,
              price: product.price?.raw,
              currency: product.currency,
              list: product.sourceType,
              position,
              variant: product.backbox_grade_value,
              merchantId: product.merchant_link_grade_v2?.params?.merchantId,
              listingId: product.listingID,
              dealType,
              source: PRODUCT_SOURCE.SEARCH,
              provider: PRODUCT_PROVIDERS.ALGOLIA,
              productAlgoliaReranked: product.productAlgoliaReranked,
            },
          }

          // TODO: [SR-2292] Clean AB test
          if (
            this.isProductLanding &&
            this.experiments?.removeFreshProducts === 'withoutFreshProducts'
          ) {
            return productTrackingDataForAlgolia
          }

          if (product.isRecommendation) {
            return toTrackingData(product.rawData, {
              dealType,
              list: PRODUCT_SOURCE_TYPE.LP_RECOS,
              position,
              provider: PRODUCT_PROVIDERS.EARLYBIRD,
              source: PRODUCT_SOURCE.RECOMMENDATIONS,
              widgetId: this.recommendationWidgetId,
            })
          }

          return productTrackingDataForAlgolia
        })

      return allResultsTrackingData
    },

    searchResults() {
      if (this.isLoading) {
        return Array(30).fill({
          isLoading: true,
          thumbnail: 'placeholder',
        })
      }

      const start =
        !this.$fetchState.pending &&
        !this.recommendations[0] &&
        this.isProductLanding
          ? 1
          : 0

      return (
        buildResultsFromAttributes({
          attributes: this.searchResultsData,
          structure: this.listViewStructure,
        })
          .slice(start)
          .map((searchResult, index) => {
            const position = this.calcProductPosition(
              index,
              this.searchResultsOffSet,
            )

            return this.searchResultToProduct(
              searchResult,
              this.linkToUse,
              this.$t(this.translations.months),
              position,
            )
          })
          // TODO: [SR-2292] Clean AB test
          .map((product, index) => {
            if (
              this.isProductLanding &&
              this.experiments?.removeFreshProducts ===
                'withoutFreshProducts' &&
              index < 3
            ) {
              return {
                ...product,
                isRecommendation: true,
              }
            }

            return product
          })
      )
    },

    recommendationProducts() {
      return !isEmpty(this.recommendations)
        ? this.getRecommendationProducts(this.recommendations.slice(1))
        : []
    },

    highlightedProduct() {
      if (this.$fetchState.pending) {
        return undefined
      }

      if (this.recommendations[0]) {
        const product = this.recommendations[0]
        const attributes = createAttributes(
          this.listViewStructure?.table,
          product,
        )

        return attraqtToHighlightedProductCardProps(product, { attributes })
      }

      if (this.searchResultsData[0] && this.isProductLanding) {
        const product = this.searchResultsData[0]
        const attributes = createAttributesOld(
          this.listViewStructure?.table,
          product,
        )

        return algoliaToHighlightedProductCardProps(product, {
          attributes,
          landingType: this.landingType,
          month: this.$t(this.translations.months),
        })
      }

      return undefined
    },

    highlightedProductWithLoading() {
      return this.isLoading
        ? {
            isLoading: true,
          }
        : this.highlightedProduct
    },

    recommendationProductsWithLoading() {
      return this.isLoading
        ? Array(NUMBER_OF_RECOMMENDATIONS).fill({
            isLoading: true,
            thumbnail: 'placeholder',
          })
        : this.recommendationProducts
    },

    showRecommendationProducts() {
      return !this.hasFilters
    },
  },
  watch: {
    // See https://eslint.vuejs.org/rules/no-async-in-computed-properties.html
    // and https://router.vuejs.org/guide/essentials/dynamic-matching.html#reacting-to-params-changes
    '$route.params': '$fetch',
  },
  mounted() {
    const serverRendered = !this.$fetchState.pending

    if (serverRendered) {
      this.track()
    }
  },
  methods: {
    track() {
      trackLandingPage({
        type: this.landingType,
      })

      setRecommendationDebugExtension(this.recommendationId)
    },

    clickOnProductThumb(product, index) {
      const { productId, isRecommendation, objectID } = product
      if (!isRecommendation) {
        trackSearchAnalytics({
          type: EVENT_TYPE.CLICK,
          objectId: objectID,
          searchQueryID: this.searchQueryID,
          tokenId: this.visitorId,
          position: index + OFFSET,
          index: this.sortDefaultValue,
        })
      }

      const trackingDataProduct = this.trackingData.find(
        (track) =>
          track.product.id === productId &&
          track.product.list === product.sourceType,
      )

      if (!isEmpty(trackingDataProduct)) {
        trackProductClick(trackingDataProduct)
      }
    },

    calcProductPosition(positionInArray, extraOffSet = 0) {
      return positionInArray + OFFSET + extraOffSet
    },

    getRecommendationProducts(recommendations) {
      return recommendations.map((product, index) =>
        toProductThumb(product, {
          attributesRules: this.listViewStructure.table,
          priceFormatter: this.$t.price,
          position: this.calcProductPosition(index, this.highlightedOffSet),
          sourceType: PRODUCT_SOURCE_TYPE.LP_RECOS,
        }),
      )
    },

    searchResultToProduct(searchResult, linkToUse, month, position = 1) {
      return {
        ...searchResult,

        grade: searchResult.backbox_grade_label,
        isRecommendation: false,
        link: searchResult[linkToUse],
        name: searchResult.thumbName,
        objectID: searchResult.objectID,
        originalPrice: {
          raw: searchResult.price_new,
          display: searchResult.price_new_with_currency,
        },
        price: {
          raw: searchResult.price,
          display: searchResult.price_with_currency,
        },
        productId: searchResult.backmarketID,
        sourceType: PRODUCT_SOURCE_TYPE.RESULTS,
        subTitleModel: searchResult.sub_title_elements,
        thumbnail: searchResult.image1,
        titleModel: searchResult.title_model,
        warranty: `${searchResult.warranty} ${month}`,
        position,
        sellerId: searchResult?.merchant_link_grade_v2?.params?.merchantId,
        listingId: searchResult.listingID,
        productAlgoliaReranked:
          // eslint-disable-next-line no-underscore-dangle
          searchResult?._rankingInfo?.promotedByReRanking ?? false,
      }
    },
  },
}
